import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle2, Loader2 } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Success page Stripe redirects to after a successful Blackout Zone unlock.
 * Confirms with the backend (belt-and-braces in case the webhook hasn't
 * landed yet), refreshes the auth context so `user.blackout_unlocked_at`
 * lights up, then routes back to /blackout.
 */
export default function BlackoutUnlockedPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [status, setStatus] = useState('verifying'); // verifying | unlocked | failed

  useEffect(() => {
    let mounted = true;
    const sessionId = params.get('session_id');
    (async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          if (mounted) setStatus('failed');
          return;
        }
        if (sessionId) {
          await axios.get(`${API}/blackout/checkout/verify`, {
            params: { session_id: sessionId },
            headers: { Authorization: `Bearer ${token}` },
          });
        }
        // Refresh user so blackout_unlocked_at hits AuthContext
        await refreshUser?.();
        if (mounted) setStatus('unlocked');
      } catch {
        if (mounted) setStatus('failed');
      }
    })();
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  return (
    <div className="min-h-screen flex items-center justify-center px-5" data-testid="blackout-unlocked-page">
      <div className="max-w-md w-full text-center space-y-6">
        {status === 'verifying' && (
          <>
            <Loader2 className="w-12 h-12 animate-spin mx-auto text-amber-400" />
            <p className="text-lg text-muted-foreground">Confirming your unlock…</p>
          </>
        )}
        {status === 'unlocked' && (
          <>
            <div className="w-20 h-20 mx-auto rounded-full bg-green-500/20 flex items-center justify-center">
              <CheckCircle2 className="w-12 h-12 text-green-400" />
            </div>
            <h1 className="text-3xl font-black">Blackout Zone unlocked 🎉</h1>
            <p className="text-muted-foreground">
              You're set for life. Offline routing, SOS posts, beacon mesh — all yours.
            </p>
            <Button
              onClick={() => navigate('/blackout')}
              className="bg-amber-500 hover:bg-amber-400 text-black font-bold"
              data-testid="goto-blackout-btn"
            >
              Open Blackout Zone
            </Button>
          </>
        )}
        {status === 'failed' && (
          <>
            <h1 className="text-2xl font-black text-red-400">Couldn't confirm payment</h1>
            <p className="text-muted-foreground">
              If you were charged, the unlock will activate within a minute via Stripe webhook.
              Refresh this page or open Blackout Zone in a moment.
            </p>
            <Button onClick={() => navigate('/blackout')} variant="outline" data-testid="retry-blackout-btn">
              Back to Blackout Zone
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
